/**
 * Owner: Haselton Baker Risk Group, LLC
 * Copyright All Rights Reserved
 */
// @flow
import type { Element } from 'react';

import React, { Component } from 'react';
// eslint-disable-next-line import/extensions
import { FaQuestionCircle } from 'react-icons/fa';
import { Popover, PopoverHeader, PopoverBody } from 'reactstrap';
import { target, popover, inner } from '#components/support/tooltips/Tip/BaseTip/index.module.scss';

type Props = {|
  className?: string,
  targetId?: string | null,
  tipId: string,
  header: string | Element<any>,
  body: string | Element<any>,
|};

type State = {
  open: boolean,
};

const OPEN_DELAY_MS = 300;
const CLOSE_DELAY_MS = 400;
const TIP_OPEN_EVENT = 'baseTipOpen';

class BaseTip extends Component<Props, State> {
  openTimer: TimeoutID | null = null;

  closeTimer: TimeoutID | null = null;

  static defaultProps: {| className: void, targetId: void |} = {
    className: undefined,
    targetId: undefined,
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      open: false,
    };
  }

  componentDidMount() {
    document.addEventListener(TIP_OPEN_EVENT, this.handleTipOpenEvent);
  }

  componentWillUnmount() {
    document.removeEventListener(TIP_OPEN_EVENT, this.handleTipOpenEvent);
    this.clearOpenTimer();
    this.clearCloseTimer();
  }

  getTipInstanceId: () => string = () => {
    const { tipId, targetId } = this.props;
    return targetId || `${tipId}TooltipTarget`;
  };

  openTip: () => void = () => {
    const tipInstanceId = this.getTipInstanceId();

    document.dispatchEvent(
      new window.CustomEvent(TIP_OPEN_EVENT, {
        detail: { tipInstanceId },
      })
    );

    this.setState({ open: true });
  };

  clearOpenTimer: () => void = () => {
    if (this.openTimer) {
      clearTimeout(this.openTimer);
      this.openTimer = null;
    }
  };

  clearCloseTimer: () => void = () => {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  };

  handleMouseEnter: () => void = () => {
    this.clearOpenTimer();
    this.clearCloseTimer();
    this.openTimer = setTimeout(() => {
      this.openTip();
      this.openTimer = null;
    }, OPEN_DELAY_MS);
  };

  handleMouseLeave: () => void = () => {
    this.clearOpenTimer();
    this.closeTimer = setTimeout(() => {
      this.setState({ open: false });
      this.closeTimer = null;
    }, CLOSE_DELAY_MS);
  };

  handlePopoverMouseEnter: () => void = () => {
    this.clearCloseTimer();
  };

  handlePopoverMouseLeave: () => void = () => {
    this.closeTimer = setTimeout(() => {
      this.setState({ open: false });
      this.closeTimer = null;
    }, CLOSE_DELAY_MS);
  };

  handleTipOpenEvent: (event: any) => void = (event: any) => {
    const nextTipInstanceId = event?.detail?.tipInstanceId;

    if (nextTipInstanceId && nextTipInstanceId !== this.getTipInstanceId()) {
      this.clearOpenTimer();
      this.clearCloseTimer();
      this.setState({ open: false });
    }
  };

  toggle: () => void = () => {
    const { open } = this.state;
    this.clearOpenTimer();

    if (open) {
      this.setState({ open: false });
      return;
    }

    this.openTip();
  };

  render(): Element<'span'> | null {
    const { body, className, header, tipId, targetId } = this.props;
    const defaultTargetId = `${tipId}TooltipTarget`;
    const { open } = this.state;
    return body ? (
      <span className={className}>
        <FaQuestionCircle
          size="1rem"
          className={target}
          id={targetId || defaultTargetId}
          onMouseEnter={this.handleMouseEnter}
          onMouseLeave={this.handleMouseLeave}
        />
        <Popover
          className={popover}
          popperClassName={popover}
          innerClassName={inner}
          placement="top"
          // We add the following modifiers to avoid bugs associated with placement flipping in
          // in Reactstrap 8.1
          modifiers={{
            preventOverflow: { boundariesElement: 'viewport' },
            flip: {
              enable: true,
              behavior: ['top', 'bottom', 'top'],
            },
          }}
          trigger="click"
          isOpen={open}
          toggle={this.toggle}
          target={targetId || defaultTargetId}
        >
          <PopoverHeader>{header}</PopoverHeader>
          <PopoverBody>
            <div
              onMouseEnter={this.handlePopoverMouseEnter}
              onMouseLeave={this.handlePopoverMouseLeave}
            >
              {typeof body === 'string' ? <p>{body}</p> : body}
            </div>
          </PopoverBody>
        </Popover>
      </span>
    ) : null;
  }
}

export default BaseTip;
